// Quotes CRUD, Sections, Reference Data, CRM Integration, and Claims APIs
import type { PaginatedResponse } from '@quotech/api'
import { getApiClient, withOrgConfig, asArray } from '../quote-api-helpers'
import type {
  QuoteListItem, QuoteDetail, QuoteSection,
  CreateQuoteRequest, UpdateQuoteRequest,
  CreateSectionRequest, UpdateSectionRequest,
  JobQuotesResponse, CreateBuilderFromSheetResponse, QuoteVersionRow,
} from '../quote-api-types'
import type {
  MasterQuote, MasterQuoteDetail, PaymentClaim, PaymentClaimDetail,
  CreatePaymentClaimRequest,
} from '../quote-api-claims-types'
import type { TierRate, Supplier, ProductCategory, Product, Application } from '../quote-api-ai-types'

export const quotesCrudApi = {
  async listQuotes(params?: { status?: string; search?: string; page?: number }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<QuoteListItem> | QuoteListItem[]>(
      '/quotebuilder/quotes/', withOrgConfig({ params })
    )
    return asArray(res.data)
  },

  async getQuote(id: string) {
    const client = getApiClient()
    const res = await client.get<QuoteDetail>(`/quotebuilder/quotes/${id}/`, withOrgConfig())
    return res.data
  },

  async createQuote(data: CreateQuoteRequest) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>('/quotebuilder/quotes/', data, withOrgConfig())
    return res.data
  },

  async updateQuote(id: string, data: UpdateQuoteRequest) {
    const client = getApiClient()
    const res = await client.patch<QuoteDetail>(`/quotebuilder/quotes/${id}/`, data, withOrgConfig())
    return res.data
  },

  async deleteQuote(id: string) {
    const client = getApiClient()
    await client.delete(`/quotebuilder/quotes/${id}/`, withOrgConfig())
  },

  /**
   * `jobId` re-homes the copy. Without it the backend inherits the SOURCE
   * quote's job, so importing an existing quote onto job B left the copy
   * sitting on job A and the import looked like it had done nothing.
   */
  async duplicateQuote(id: string, asRevision = false, jobId?: number | string | null) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(
      `/quotebuilder/quotes/${id}/duplicate/`,
      { as_revision: asRevision, ...(jobId ? { job_id: Number(jobId) } : {}) },
      withOrgConfig(),
    )
    return res.data
  },

  // Job-grouped quotes for the in-builder tab strip / version switcher.
  // Returns the full commercial picture of a job (contract + variations +
  // builder-native), including fileless variations that have no builder scope.
  async getJobQuotes(jobId: number | string) {
    const client = getApiClient()
    const res = await client.get<JobQuotesResponse>(
      `/quotebuilder/jobs/${jobId}/quotes/`, withOrgConfig()
    )
    return res.data
  },

  // "Add scope" on a fileless/unlinked variation tab: mints an editable
  // builder shell from the QuoteSheet (idempotent) and returns its id.
  async createBuilderQuoteFromSheet(jobId: number | string, sheetId: number) {
    const client = getApiClient()
    const res = await client.post<CreateBuilderFromSheetResponse>(
      `/quotebuilder/jobs/${jobId}/quotes/from-sheet/`, { sheet_id: sheetId }, withOrgConfig()
    )
    return res.data
  },

  async listQuoteVersions(id: string) {
    const client = getApiClient()
    const res = await client.get<{ versions?: QuoteVersionRow[] }>(
      `/quotebuilder/quotes/${id}/versions/`, withOrgConfig()
    )
    return res.data?.versions || []
  },

  async submitForApproval(id: string) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(`/quotebuilder/quotes/${id}/submit_for_approval/`, {}, withOrgConfig())
    return res.data
  },

  async markSent(id: string) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(`/quotebuilder/quotes/${id}/mark_sent/`, {}, withOrgConfig())
    return res.data
  },

  async markAccepted(id: string, params?: { reference?: string }) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(
      `/quotebuilder/quotes/${id}/mark_accepted/`, { reference: params?.reference || '' }, withOrgConfig()
    )
    return res.data
  },

  async markRejected(id: string, params?: { reason?: string }) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(
      `/quotebuilder/quotes/${id}/mark_rejected/`, { reason: params?.reason || '' }, withOrgConfig()
    )
    return res.data
  },

  async recalculateQuote(id: string) {
    const client = getApiClient()
    const res = await client.post<QuoteDetail>(`/quotebuilder/quotes/${id}/recalculate/`, {}, withOrgConfig())
    return res.data
  },

  async exportQuote(id: string, format: 'pdf' | 'excel') {
    const client = getApiClient()
    const res = await client.get(`/quotebuilder/quotes/${id}/export/${format}/`, withOrgConfig({ responseType: 'blob' }))
    return res.data as Blob
  },

  // Sections
  async createSection(quoteId: string, data: CreateSectionRequest) {
    const client = getApiClient()
    const res = await client.post<QuoteSection>(`/quotebuilder/quotes/${quoteId}/sections/`, data, withOrgConfig())
    return res.data
  },

  async updateSection(quoteId: string, sectionId: string, data: UpdateSectionRequest) {
    const client = getApiClient()
    const res = await client.patch<QuoteSection>(
      `/quotebuilder/quotes/${quoteId}/sections/${sectionId}/`, data, withOrgConfig()
    )
    return res.data
  },

  async deleteSection(quoteId: string, sectionId: string) {
    const client = getApiClient()
    await client.delete(`/quotebuilder/quotes/${quoteId}/sections/${sectionId}/`, withOrgConfig())
  },

  // Reference data
  async listTierRates() {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<TierRate> | TierRate[]>(`/quotebuilder/tier_rates/`, withOrgConfig())
    return asArray(res.data)
  },

  async listSuppliers(params?: { search?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<Supplier> | Supplier[]>(`/quotebuilder/suppliers/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async listProductCategories() {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<ProductCategory> | ProductCategory[]>(`/quotebuilder/product_categories/`, withOrgConfig())
    return asArray(res.data)
  },

  async listProducts(params?: { search?: string; supplier?: string; category?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<Product> | Product[]>(`/quotebuilder/products/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async listApplications(params?: { search?: string }) {
    const client = getApiClient()
    const res = await client.get<PaginatedResponse<Application> | Application[]>(`/quotebuilder/applications/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  // CRM / Quote Tracker integration
  async linkToTracker(params: { builder_quote_id: string; tracker_quote_id: string }) {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/integration/link/`, params, withOrgConfig())
    return res.data as unknown
  },

  async unlinkFromTracker(params: { builder_quote_id: string }) {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/integration/unlink/`, params, withOrgConfig())
    return res.data as unknown
  },

  async getLinkStatus(params: { builder_quote_id?: string; tracker_quote_id?: string }) {
    const client = getApiClient()
    const res = await client.get(`/quotebuilder/integration/status/`, withOrgConfig({ params }))
    return res.data as unknown
  },

  async createFromTracker(params: { tracker_quote_id: string }) {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/integration/create-from-tracker/`, params, withOrgConfig())
    return res.data as unknown
  },

  // MasterQuote & Claims
  async listMasterQuotes(params?: { status?: string }): Promise<MasterQuote[]> {
    const client = getApiClient()
    const res = await client.get<MasterQuote[]>(`/quotebuilder/master-quotes/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async getMasterQuote(id: string): Promise<MasterQuoteDetail> {
    const client = getApiClient()
    const res = await client.get<MasterQuoteDetail>(`/quotebuilder/master-quotes/${id}/`, withOrgConfig())
    return res.data
  },

  async createMasterQuoteFromQuote(quoteId: string): Promise<MasterQuoteDetail> {
    const client = getApiClient()
    const res = await client.post<MasterQuoteDetail>(`/quotebuilder/master-quotes/from-quote/`, { quote_id: quoteId }, withOrgConfig())
    return res.data
  },

  async listPaymentClaims(masterQuoteId?: string): Promise<PaymentClaim[]> {
    const client = getApiClient()
    const params: Record<string, string> = {}
    if (masterQuoteId) params.master_quote = masterQuoteId
    const res = await client.get<PaymentClaim[]>(`/quotebuilder/payment-claims/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async getPaymentClaim(id: string): Promise<PaymentClaimDetail> {
    const client = getApiClient()
    const res = await client.get<PaymentClaimDetail>(`/quotebuilder/payment-claims/${id}/`, withOrgConfig())
    return res.data
  },

  async createPaymentClaim(data: CreatePaymentClaimRequest): Promise<PaymentClaimDetail> {
    const client = getApiClient()
    const res = await client.post<PaymentClaimDetail>(`/quotebuilder/payment-claims/`, data, withOrgConfig())
    return res.data
  },

  async submitPaymentClaim(id: string): Promise<PaymentClaimDetail> {
    const client = getApiClient()
    const res = await client.post<PaymentClaimDetail>(`/quotebuilder/payment-claims/${id}/submit/`, {}, withOrgConfig())
    return res.data
  },

  async certifyPaymentClaim(id: string, data: { certified_amount: number; notes?: string }): Promise<PaymentClaimDetail> {
    const client = getApiClient()
    const res = await client.post<PaymentClaimDetail>(`/quotebuilder/payment-claims/${id}/certify/`, data, withOrgConfig())
    return res.data
  },

  async createVariation(masterQuoteId: string, projectName: string): Promise<{ id: string; quote_number: string }> {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/variations/create/`, { master_quote_id: masterQuoteId, project_name: projectName }, withOrgConfig())
    return res.data as { id: string; quote_number: string }
  },

  async approveVariation(variationId: string): Promise<{ id: string; status: string; new_contract_value: string }> {
    const client = getApiClient()
    const res = await client.post(`/quotebuilder/variations/${variationId}/approve/`, {}, withOrgConfig())
    return res.data as { id: string; status: string; new_contract_value: string }
  },
}
